// Constants and helpers ported from the source App.tsx
// Keeps domain logic (PM POSHAN data shape, computations, classifiers) out of the UI.

export const STORAGE_KEY = "pm-poshan-quarterly-uc-state-v2";

export const QUARTERS = {
  Q1: { label: "Q1 (Apr-Jun)", months: ["April", "May", "June"] },
  Q2: { label: "Q2 (Jul-Sep)", months: ["July", "August", "September"] },
  Q3: { label: "Q3 (Oct-Dec)", months: ["October", "November", "December"] },
  Q4: { label: "Q4 (Jan-Mar)", months: ["January", "February", "March"] },
};

export const FUND_COMPONENTS = [
  { key: "balVatika", label: "Cooking Cost - Bal Vatika" },
  { key: "primary", label: "Cooking Cost - Primary" },
  { key: "upperPrimary", label: "Cooking Cost - Upper Primary" },
];

export const CLASS_CATEGORIES = [
  { key: "balVatika", label: "Bal Vatika", enrollmentKey: "balVatikaEnrollment" },
  { key: "primary", label: "Primary", enrollmentKey: "primaryEnrollment" },
  { key: "upperPrimary", label: "Upper Primary", enrollmentKey: "upperPrimaryEnrollment" },
];

export const GRAIN_ITEMS = [
  { key: "balVatikaRice", category: "Bal Vatika", item: "Rice" },
  { key: "primaryRice", category: "Primary", item: "Rice" },
  { key: "upperPrimaryRice", category: "Upper Primary", item: "Rice" },
  { key: "balVatikaWheat", category: "Bal Vatika", item: "Wheat" },
  { key: "primaryWheat", category: "Primary", item: "Wheat" },
  { key: "upperPrimaryWheat", category: "Upper Primary", item: "Wheat" },
];

export const DEFAULT_INGREDIENTS = [
  { id: "dali", name: "Pulses / ଡାଲି", quantity: 0, unit: "kg", rate: 0 },
  { id: "soyabati", name: "Soya Chunks / ସୋୟାବତି", quantity: 0, unit: "kg", rate: 0 },
  { id: "anda", name: "Eggs / ଅଣ୍ଡା", quantity: 0, unit: "pcs", rate: 0 },
  { id: "pariba", name: "Vegetables / ପରିବା", quantity: 0, unit: "kg", rate: 0 },
  { id: "tela", name: "Oil / ତେଲ", quantity: 0, unit: "litre", rate: 0 },
  { id: "masala-luna", name: "Spices and Salt / ମସଲା ଓ ଲୁଣ", quantity: 0, unit: "kg", rate: 0 },
  { id: "jaleni", name: "Fuel / ଜାଳେଣୀ", quantity: 0, unit: "month", rate: 0 },
  { id: "mandia", name: "Ragi/Millet Flour / ମାଣ୍ଡିଆ ଚୁନା", quantity: 0, unit: "kg", rate: 0 },
  { id: "gahama", name: "Wheat Flour / ଗହମ ଚୁନା", quantity: 0, unit: "kg", rate: 0 },
  { id: "guda", name: "Jaggery / ଗୁଡ଼", quantity: 0, unit: "kg", rate: 0 },
  { id: "rashi", name: "Sesame / ରାଶି", quantity: 0, unit: "kg", rate: 0 },
  { id: "chinabadama", name: "Peanuts / ଚିନାବାଦାମ", quantity: 0, unit: "kg", rate: 0 },
  { id: "gujurati", name: "Cardamom Powder / ଗୁଜୁରାତି ଗୁଣ୍ଡ", quantity: 0, unit: "kg", rate: 0 },
  { id: "ladoo-making", name: "Ladoo Making Charge", quantity: 0, unit: "service", rate: 0 },
];

export const ODIA_RECEIPT_GROUPS = [
  { key: "grocery", title: "ଗ୍ରୋସେରୀ ହାତ ରସିଦ", english: "Grocery" },
  { key: "vegetables", title: "ପରିବା ହାତ ରସିଦ", english: "Vegetables" },
  { key: "egg", title: "ଅଣ୍ଡା ହାତ ରସିଦ", english: "Egg" },
  { key: "fuel", title: "ଜାଳେଣୀ ହାତ ରସିଦ", english: "Fuel" },
  { key: "nutritionLadoo", title: "ପୁଷ୍ଟି ଲଡୁ ହାତ ରସିଦ", english: "Nutrition Ladoo" },
  {
    key: "ladooMaking",
    title: "ଲଡୁ ପ୍ରସ୍ତୁତି ଖର୍ଚ୍ଚ ହାତ ରସିଦ",
    english: "Ladoo Making Charge",
    note: "ଏହି ଲଡୁ ପ୍ରସ୍ତୁତି ଖର୍ଚ୍ଚ ରୋଷେୟା/ସହାୟିକା (CCH) କୁ ପ୍ରଦାନ କରାଗଲା ।",
  },
];

const numberFormatter = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const compactFormatter = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 3,
});

export function toNumber(value) {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function money(value) {
  return `Rs ${numberFormatter.format(value || 0)}`;
}

export function qty(value) {
  return compactFormatter.format(value || 0);
}

export function percent(value) {
  if (!Number.isFinite(value)) return "0%";
  return `${compactFormatter.format(value)}%`;
}

export function ingredientAmount(item) {
  // For daily-aggregated ingredients, the exact summed amount is preserved on
  // the row (sum of qty_i × rate_i across days). Prefer it so the Annexure-1
  // grand total equals the deterministic Fund-Management 3-month consumed
  // (both derive from the same per-day meal-plan breakdown). For manual
  // ingredients (Generator quick-entry / overrides), fall back to qty × rate.
  if (item && item.amount !== undefined && item.amount !== null && item.amount !== "") {
    const a = toNumber(item.amount);
    if (a !== 0) return a;
  }
  return toNumber(item.quantity) * toNumber(item.rate);
}

export function safeFileName(value) {
  return (value || "PM_POSHAN").replace(/[^a-z0-9-_]+/gi, "_").replace(/^_+|_+$/g, "");
}

export function emptyFunds() {
  return {
    balVatika: { opening: 0, received: 0, expenditure: 0 },
    primary: { opening: 0, received: 0, expenditure: 0 },
    upperPrimary: { opening: 0, received: 0, expenditure: 0 },
  };
}

export function emptyGrains() {
  return {
    balVatikaRice: { opening: 0, received: 0, consumption: 0 },
    primaryRice: { opening: 0, received: 0, consumption: 0 },
    upperPrimaryRice: { opening: 0, received: 0, consumption: 0 },
    balVatikaWheat: { opening: 0, received: 0, consumption: 0 },
    primaryWheat: { opening: 0, received: 0, consumption: 0 },
    upperPrimaryWheat: { opening: 0, received: 0, consumption: 0 },
  };
}

export function cloneIngredients() {
  return DEFAULT_INGREDIENTS.map((item) => ({ ...item }));
}

export function createMonth(monthName) {
  return {
    monthName,
    meals: {
      balVatikaEnrollment: 0,
      primaryEnrollment: 0,
      upperPrimaryEnrollment: 0,
      workingDays: 0,
      mdmServedDays: 0,
      totalMealsServed: 0,
    },
    funds: emptyFunds(),
    grains: emptyGrains(),
    ingredients: cloneIngredients(),
    cooks: [
      { id: "cook-1", name: "", amount: 0, mode: "Bank" },
      { id: "cook-2", name: "", amount: 0, mode: "Bank" },
    ],
  };
}

export function defaultVendorInfo() {
  return {
    groceryVendor: "", groceryFather: "", groceryVillage: "", groceryAddress: "",
    vegetableVendor: "", vegetableFather: "", vegetableVillage: "", vegetableAddress: "",
    eggVendor: "", eggFather: "", eggVillage: "", eggAddress: "",
    fuelVendor: "", fuelFather: "", fuelVillage: "", fuelAddress: "",
    ladooVendor: "", ladooFather: "", ladooVillage: "", ladooAddress: "",
  };
}

export function defaultState() {
  // Compute current FY (Apr–Mar) + quarter from today.
  const d = new Date();
  const m = d.getMonth(); // 0=Jan
  const y = d.getFullYear();
  const startYear = m >= 3 ? y : y - 1;
  const endYY = String((startYear + 1) % 100).padStart(2, "0");
  const financialYear = `${startYear}-${endYY}`;
  const quarter =
    m >= 3 && m <= 5 ? "Q1" :
    m >= 6 && m <= 8 ? "Q2" :
    m >= 9 && m <= 11 ? "Q3" : "Q4";
  return {
    schoolInfo: {
      schoolName: "",
      udiseCode: "",
      district: "",
      block: "",
      village: "",
      financialYear,
      quarter,
      departmentName: "School & Mass Education Department",
      schemeName: "PM POSHAN",
      sanctionNoDate: "",
      reportDate: (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      })(),
      previousSanctionNoDate: "",
      previousSanctionedAmount: 0,
      previousUnspentBalance: 0,
      previousUtilizedAmount: 0,
      sanctionedAmount: 0,
      sanctionOrderNo: "",
      sanctionDate: "",
      bankAccountNo: "",
      bankName: "",
      ifscCode: "",
    },
    vendorInfo: defaultVendorInfo(),
    months: QUARTERS[quarter].months.map(createMonth),
  };
}

export function loadLocalState() {
  if (typeof window === "undefined") return defaultState();
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultState();
    const parsed = JSON.parse(saved);
    if (parsed?.schoolInfo && Array.isArray(parsed.months) && parsed.months.length === 3) {
      const fallback = defaultState();
      return {
        schoolInfo: { ...fallback.schoolInfo, ...parsed.schoolInfo },
        vendorInfo: { ...fallback.vendorInfo, ...(parsed.vendorInfo || {}) },
        months: parsed.months.map((month) => ({
          ...month,
          ingredients: (month.ingredients || []).filter(
            (item) => item.id !== "ladoo-prastuti" && !item.name.toLowerCase().includes("ladoo prastuti"),
          ),
        })),
      };
    }
  } catch {
    return defaultState();
  }
  return defaultState();
}

export function saveLocalState(state) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function clearLocalState() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
}

export function classifyReceiptIngredient(item) {
  const text = `${item.id} ${item.name}`.toLowerCase();
  if (
    text.includes("ladoo-making") || text.includes("ladoo prastuti") ||
    text.includes("making charge") || text.includes("ପ୍ରସ୍ତୁତି") || text.includes("ରୋଷେୟା")
  ) return "ladooMaking";
  if (text.includes("anda") || text.includes("egg") || text.includes("ଅଣ୍ଡା")) return "egg";
  if (text.includes("pariba") || text.includes("vegetable") || text.includes("ପରିବା")) return "vegetables";
  if (text.includes("jaleni") || text.includes("fuel") || text.includes("ଜାଳେଣୀ")) return "fuel";
  if (
    text.includes("mandia") || text.includes("ragi") || text.includes("gahama") ||
    text.includes("wheat flour") || text.includes("guda") || text.includes("jaggery") ||
    text.includes("rashi") || text.includes("sesame") || text.includes("chinabadama") ||
    text.includes("peanut") || text.includes("groundnut") || text.includes("ground nut") ||
    text.includes("gujurati") || text.includes("cardamom") ||
    text.includes("sugar") || text.includes("ଚିନି") || text.includes("chini") ||
    text.includes("refined oil") || text.includes("refined-oil") ||
    text.includes("ମାଣ୍ଡିଆ") || text.includes("ଗହମ") || text.includes("ଗୁଡ") ||
    text.includes("ରାଶି") || text.includes("ଚିନାବାଦାମ") || text.includes("ଗୁଜୁରାତି")
  ) return "nutritionLadoo";
  return "grocery";
}

export function displayIngredientName(item) {
  const canonical = DEFAULT_INGREDIENTS.find((ingredient) => ingredient.id === item.id)?.name;
  if (canonical) return canonical;
  return item.name;
}

export function categoryMeals(month) {
  const byCategory = CLASS_CATEGORIES.map((category) => ({
    ...category,
    meals: toNumber(month.meals[category.enrollmentKey]) * toNumber(month.meals.mdmServedDays),
  }));
  const total = byCategory.reduce((sum, item) => sum + item.meals, 0) || 1;
  return byCategory.map((item) => ({ ...item, share: item.meals / total }));
}

export function vendorForGroup(vendors, key) {
  const make = (prefix) => ({
    name: vendors[`${prefix}Vendor`] || "",
    nameOdia: vendors[`${prefix}VendorOdia`] || "",
    father: vendors[`${prefix}Father`] || "",
    fatherOdia: vendors[`${prefix}FatherOdia`] || "",
    relation: vendors[`${prefix}Relation`] === "husband" ? "husband" : "father",
    village: vendors[`${prefix}Village`] || "",
    villageOdia: vendors[`${prefix}VillageOdia`] || "",
    address: vendors[`${prefix}Address`] || "",
    addressOdia: vendors[`${prefix}AddressOdia`] || "",
    post: vendors[`${prefix}Post`] || "",
    postOdia: vendors[`${prefix}PostOdia`] || "",
    thana: vendors[`${prefix}Thana`] || "",
    thanaOdia: vendors[`${prefix}ThanaOdia`] || "",
    block: vendors[`${prefix}Block`] || "",
    blockOdia: vendors[`${prefix}BlockOdia`] || "",
    district: vendors[`${prefix}District`] || "",
    districtOdia: vendors[`${prefix}DistrictOdia`] || "",
    pincode: vendors[`${prefix}Pincode`] || "",
    pincodeOdia: vendors[`${prefix}PincodeOdia`] || "",
  });
  if (key === "vegetables") return make("vegetable");
  if (key === "egg") return make("egg");
  if (key === "fuel") return make("fuel");
  // The Pushti / Nutrition Ladoo receipt is issued to the Ladoo vendor when
  // the school has one set up; otherwise we fall back to the grocery vendor
  // (which has historically supplied the dry-ladoo ingredients).
  if (key === "nutritionLadoo" || key === "ladoo") {
    const ladoo = make("ladoo");
    if ((ladoo.name || "").trim()) return ladoo;
    return make("grocery");
  }
  return make("grocery");
}
